import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChapterMarker, RevealText } from './shared';
import { STOCK, NAPPA_TEXTURE } from '../assets';
import * as sfx from '../sfx';

const GRADES = [
  {
    name: 'Full-Grain',
    img: STOCK.darkTexture,
    grain: 'Untouched natural grain',
    softness: 92,
    durability: 98,
    note: 'The finest hides. Natural markings preserved — each jacket unique.',
  },
  {
    name: 'Nappa Lamb',
    img: NAPPA_TEXTURE,
    grain: 'Buttery, fine-pored surface',
    softness: 98,
    durability: 84,
    note: 'Featherweight softness prized by Japanese and Korean fashion houses.',
  },
  {
    name: 'Aniline Cow',
    img: STOCK.brownStitched,
    grain: 'Rich, dyed-through depth',
    softness: 80,
    durability: 95,
    note: 'Deep colour that ages into character. Built for decades of wear.',
  },
];

const Bar = ({ label, value }) => (
  <div className="mb-3">
    <div className="flex justify-between font-accent text-[9px] text-[#A3A3A3] mb-1.5">
      <span>{label}</span><span className="text-[#B89B72]">{value}</span>
    </div>
    <div className="h-px bg-[#2A2A2A] relative overflow-hidden">
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: value / 100 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="absolute inset-0 bg-[#B89B72] origin-left"
      />
    </div>
  </div>
);

/* ── 3D Tilt Card with Glare ─────────────────────────────────── */
function TiltCard({ children, className, ...props }) {
  const cardRef = useRef(null);
  const glareRef = useRef(null);

  const handleMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -12;
    const rotateY = ((x - centerX) / centerX) * 12;

    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03, 1.03, 1.03)`;

    if (glareRef.current) {
      const px = (x / rect.width) * 100;
      const py = (y / rect.height) * 100;
      glareRef.current.style.background = `radial-gradient(circle at ${px}% ${py}%, rgba(184,155,114,0.25) 0%, transparent 60%)`;
      glareRef.current.style.opacity = '1';
    }
  }, []);

  const handleLeave = useCallback(() => {
    const card = cardRef.current;
    if (card) card.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    if (glareRef.current) glareRef.current.style.opacity = '0';
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ transition: 'transform 0.15s ease-out', willChange: 'transform', transformStyle: 'preserve-3d' }}
      {...props}
    >
      {children}
      <div
        ref={glareRef}
        className="absolute inset-0 pointer-events-none z-30 opacity-0"
        style={{ transition: 'opacity 0.3s ease' }}
      />
    </div>
  );
}

export default function Ch2Selection() {
  return (
    <section id="ch-2" data-testid="chapter-2" className="relative min-h-screen bg-[#0A0A0A] px-6 md:px-16 py-28 md:py-40">
      <ChapterMarker num="02" title="Leather Selection" />
      <div className="md:flex md:items-end md:justify-between mb-14 md:mb-20">
        <RevealText className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
          Only one hide in ten<br /><span className="italic text-[#B89B72]">makes the cut.</span>
        </RevealText>
        <p className="font-body font-light text-sm text-[#A3A3A3] max-w-xs mt-6 md:mt-0 leading-relaxed">
          Run your hand over each grade. Hover to feel the grain, the softness, the strength.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
        {GRADES.map((g, i) => (
          <motion.div
            key={g.name}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.15 }}
          >
            <TiltCard
              data-testid={`leather-grade-${i + 1}`}
              data-hover
              onMouseEnter={() => sfx.rustle()}
              className="group relative overflow-hidden border border-[#2A2A2A] aspect-[3/4]"
            >
              <img
                src={g.img}
                alt={g.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <span className="font-accent text-[9px] text-[#8C5A35] block mb-2">Grade {String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-display text-3xl font-light text-[#F5F5F0] mb-1">{g.name}</h3>
                <p className="font-body text-xs text-[#A3A3A3]">{g.grain}</p>
                <div className="max-h-52 opacity-100 md:max-h-0 md:opacity-0 md:group-hover:max-h-52 md:group-hover:opacity-100 transition-[max-height,opacity] duration-700 ease-out overflow-hidden">
                  <div className="pt-5">
                    <Bar label="Softness" value={g.softness} />
                    <Bar label="Durability" value={g.durability} />
                    <p className="font-body text-xs text-[#F5F5F0]/70 leading-relaxed mt-4">{g.note}</p>
                  </div>
                </div>
              </div>
            </TiltCard>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
